// src/tools/basemap/basemapTool.js
import maplibregl from 'maplibre-gl';
import { center as turfCenter, distance as turfDistance, bearing as turfBearing, destination as turfDestination } from '@turf/turf';

const DB_NAME = 'SPD_Overlay_DB';
const STORE_NAME = 'overlay_store';
const DB_KEY = 'current_overlay';

export const basemapTool = {
    _map: null,
    _onOverlayUpdate: null,
    _fileInput: null,
    _overlayData: null, // { image_url, coordinates, opacity }
    _markers: [],
    _centerMarker: null,
    _isDragging: false,
    _draggedMarker: null,
    _dragStartCoords: null,
    _dragStartMouse: null,
    _controlPanel: null,
    _originalCoordinates: null,

    // Инициализация базы данных IndexedDB
    async _initDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            };
            request.onsuccess = (e) => resolve(e.target.result);
            request.onerror = (e) => reject(e.target.error);
        });
    },

    async _saveToDB(data) {
        const db = await this._initDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.put(data, DB_KEY);
            request.onsuccess = () => resolve();
            request.onerror = (e) => reject(e.target.error);
        });
    },

    async _loadFromDB() {
        const db = await this._initDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(DB_KEY);
            request.onsuccess = (e) => resolve(e.target.result);
            request.onerror = (e) => reject(e.target.error);
        });
    },

    async _removeFromDB() {
        const db = await this._initDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.delete(DB_KEY);
            request.onsuccess = () => resolve();
            request.onerror = (e) => reject(e.target.error);
        });
    },

    async initialize(map, options) {
        this._map = map;
        this._onOverlayUpdate = options.onOverlayUpdate;

        this.onCornerMouseMove = this.onCornerMouseMove.bind(this);
        this.onCenterMouseMove = this.onCenterMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);

        // Загружаем сохраненные данные из IndexedDB
        try {
            const savedData = await this._loadFromDB();
            if (savedData) {
                this._overlayData = savedData;
                this._onOverlayUpdate(this._overlayData);
            }
        } catch (e) {
            console.error('Failed to load overlay from IndexedDB:', e);
        }

        this._fileInput = document.createElement('input');
        this._fileInput.type = 'file';
        this._fileInput.accept = 'image/*';
        this._fileInput.style.display = 'none';
        this._fileInput.onchange = this.handleFileChange.bind(this);
        document.body.appendChild(this._fileInput);
    },

    activate() {
        console.log('Basemap tool activated');
        this._originalCoordinates = this._overlayData ? JSON.parse(JSON.stringify(this._overlayData.coordinates)) : null;
        if (this._overlayData) {
            this.showControls();
        } else {
            this._fileInput.click();
        }
    },

    deactivate() {
        console.log('Basemap tool deactivated');
        if (this._map.dragPan.isEnabled() === false) {
            this._map.dragPan.enable();
        }
        this.cleanup();
    },

    cleanup() {
        this._markers.forEach(m => m.remove());
        this._markers = [];
        if (this._centerMarker) {
            this._centerMarker.remove();
            this._centerMarker = null;
        }
        if (this._controlPanel) {
            this._controlPanel.remove();
            this._controlPanel = null;
        }
    },

    handleFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageUrl = event.target.result;
                const img = new Image();
                img.onload = () => {
                    const imgWidth = img.width;
                    const imgHeight = img.height;
                    const center = this._map.getCenter();
                    const latRad = center.lat * Math.PI / 180;
                    const metersPerDegreeLat = 111320;
                    const metersPerDegreeLng = 111320 * Math.cos(latRad);
                    const widthDeg = imgWidth / metersPerDegreeLng;
                    const heightDeg = imgHeight / metersPerDegreeLat;

                    this._overlayData = {
                        image_url: imageUrl,
                        coordinates: [
                            [center.lng - widthDeg / 2, center.lat + heightDeg / 2], // top-left
                            [center.lng + widthDeg / 2, center.lat + heightDeg / 2], // top-right
                            [center.lng + widthDeg / 2, center.lat - heightDeg / 2], // bottom-right
                            [center.lng - widthDeg / 2, center.lat - heightDeg / 2]  // bottom-left
                        ],
                        opacity: 0.75,
                        rotation: 0
                    };
                    
                    this._originalCoordinates = JSON.parse(JSON.stringify(this._overlayData.coordinates));
                    if (this._onOverlayUpdate) this._onOverlayUpdate(this._overlayData);
                    this.showControls();
                };
                img.src = imageUrl;
            };
            reader.readAsDataURL(file);
        }
        this._fileInput.value = '';
    },

    showControls() {
        this.cleanup();
        if (!this._overlayData) return;

        if (this._overlayData.opacity === undefined) this._overlayData.opacity = 0.75;

        const cornerStyles = {
            width: '16px', height: '16px',
            backgroundColor: 'rgba(100, 100, 255, 0.9)',
            border: '2px solid white', borderRadius: '50%',
            cursor: 'nwse-resize', boxShadow: '0 0 8px rgba(0,0,0,0.6)'
        };

        this._overlayData.coordinates.forEach((coord, index) => {
            const el = document.createElement('div');
            Object.assign(el.style, cornerStyles);
            const marker = new maplibregl.Marker({ element: el, draggable: false }).setLngLat(coord).addTo(this._map);
            el.addEventListener('mousedown', (e) => this.onCornerMouseDown(e, marker, index));
            this._markers.push(marker);
        });

        const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
        const centerEl = document.createElement('div');
        Object.assign(centerEl.style, { ...cornerStyles, backgroundColor: 'rgba(255, 100, 100, 0.9)', cursor: 'move' });
        this._centerMarker = new maplibregl.Marker({ element: centerEl, draggable: false }).setLngLat(center).addTo(this._map);
        centerEl.addEventListener('mousedown', (e) => this.onCenterMouseDown(e));

        this._controlPanel = document.createElement('div');
        this._controlPanel.style.cssText = `
            position: absolute; top: 10px; left: 10px; background: rgba(255, 255, 255, 0.95);
            padding: 10px 15px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            z-index: 1000; display: flex; gap: 15px; align-items: center; font-family: sans-serif; font-size: 12px;
        `;

        this._controlPanel.innerHTML = `
            <div style="display: flex; flex-direction: column; gap: 10px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                    <label style="width: 80px;">Прозрачность:</label>
                    <input type="range" id="opacity-slider" min="0" max="1" step="0.05" value="${this._overlayData.opacity}" style="width: 100px;">
                    <span id="opacity-value" style="width: 35px;">${Math.round(this._overlayData.opacity * 100)}%</span>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                    <label style="width: 80px;">Вращение:</label>
                    <button id="btn-rotate-ccw" title="Повернуть влево">-15°</button>
                    <input type="range" id="rotate-slider" min="-180" max="180" step="1" value="0" style="width: 100px;">
                    <button id="btn-rotate-cw" title="Повернуть вправо">+15°</button>
                </div>
                <div style="display: flex; gap: 10px; border-top: 1px solid #ddd; pt: 10px; margin-top: 5px; padding-top: 8px;">
                    <div style="display: flex; gap: 5px; flex-grow: 1;">
                        <button id="btn-zoom-in" title="Увеличить" style="width: 30px;">+</button>
                        <button id="btn-zoom-out" title="Уменьшить" style="width: 30px;">-</button>
                    </div>
                    <div style="display: flex; gap: 5px;">
                        <button id="btn-save" style="background: #34C759; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer;">Сохранить</button>
                        <button id="btn-remove" style="background: #FF3B30; color: white; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer;">Удалить</button>
                        <button id="btn-cancel" style="padding: 4px 10px; border-radius: 4px; cursor: pointer;">X</button>
                    </div>
                </div>
            </div>
        `;

        this._map.getContainer().appendChild(this._controlPanel);

        const slider = this._controlPanel.querySelector('#opacity-slider');
        const opacityValue = this._controlPanel.querySelector('#opacity-value');
        slider.oninput = (e) => {
            const value = parseFloat(e.target.value);
            this._overlayData.opacity = value;
            opacityValue.textContent = Math.round(value * 100) + '%';
            if (this._onOverlayUpdate) this._onOverlayUpdate(this._overlayData);
        };

        this._controlPanel.querySelector('#btn-zoom-in').onclick = () => this.scaleOverlay(1.02);
        this._controlPanel.querySelector('#btn-zoom-out').onclick = () => this.scaleOverlay(0.98);
        
        const rotateSlider = this._controlPanel.querySelector('#rotate-slider');
        let lastRotationValue = 0;
        if (rotateSlider) {
            rotateSlider.oninput = (e) => {
                const value = parseFloat(e.target.value);
                this.rotateOverlay(value - lastRotationValue);
                lastRotationValue = value;
            };
        }
        this._controlPanel.querySelector('#btn-rotate-ccw').onclick = () => this.rotateOverlay(-15);
        this._controlPanel.querySelector('#btn-rotate-cw').onclick = () => this.rotateOverlay(15);
        this._controlPanel.querySelector('#btn-save').onclick = () => this.save();
        this._controlPanel.querySelector('#btn-cancel').onclick = () => this.cancel();
        this._controlPanel.querySelector('#btn-remove').onclick = () => this.remove();
    },

    onCornerMouseDown(e, marker, index) {
        e.preventDefault(); e.stopPropagation();
        this._isDragging = true;
        this._draggedMarker = marker;
        this._dragStartCoords = { cornerIndex: index, coordinates: JSON.parse(JSON.stringify(this._overlayData.coordinates)) };
        const rect = this._map.getCanvas().getBoundingClientRect();
        this._dragStartMouse = this._map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
        this._map.on('mousemove', this.onCornerMouseMove);
        this._map.on('mouseup', this.onMouseUp);
    },

    onCenterMouseDown(e) {
        e.preventDefault(); e.stopPropagation();
        this._isDragging = true;
        this._draggedMarker = 'center';
        this._dragStartCoords = { coordinates: JSON.parse(JSON.stringify(this._overlayData.coordinates)) };
        const rect = this._map.getCanvas().getBoundingClientRect();
        this._dragStartMouse = this._map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
        this._map.getCanvas().style.cursor = 'grabbing';
        this._map.dragPan.disable();
        this._map.on('mousemove', this.onCenterMouseMove);
        this._map.on('mouseup', this.onMouseUp);
    },

    onCornerMouseMove(e) {
        if (!this._isDragging || this._draggedMarker === 'center') return;
        const { cornerIndex, coordinates } = this._dragStartCoords;
        const currentLngLat = e.lngLat;
        const center = turfCenter({ type: 'Polygon', coordinates: [coordinates] }).geometry.coordinates;
        const initialMarkerCoord = coordinates[cornerIndex];
        let updatedCoordinates = JSON.parse(JSON.stringify(coordinates));

        if (e.originalEvent.shiftKey) { // Вращение
            const initialBearing = turfBearing(center, initialMarkerCoord);
            const currentBearing = turfBearing(center, [currentLngLat.lng, currentLngLat.lat]);
            const rotateAngle = currentBearing - initialBearing;
            updatedCoordinates = coordinates.map(coord => {
                const b = turfBearing(center, coord);
                const d = turfDistance(center, coord);
                const dest = turfDestination(center, d, b + rotateAngle);
                return dest.geometry.coordinates;
            });
        } else if (e.originalEvent.ctrlKey) { // Масштабирование
            const initialDistance = turfDistance(center, initialMarkerCoord);
            const currentDistance = turfDistance(center, [currentLngLat.lng, currentLngLat.lat]);
            const scaleFactor = currentDistance / initialDistance;
            updatedCoordinates = coordinates.map(coord => {
                const b = turfBearing(center, coord);
                const d = turfDistance(center, coord);
                const dest = turfDestination(center, d * scaleFactor, b);
                return dest.geometry.coordinates;
            });
        } else {
            updatedCoordinates[cornerIndex] = [currentLngLat.lng, currentLngLat.lat];
        }
        this._overlayData.coordinates = updatedCoordinates;
        this.updateOverlayAndMarkers();
    },

    onCenterMouseMove(e) {
        if (!this._isDragging || this._draggedMarker !== 'center') return;
        const deltaLng = e.lngLat.lng - this._dragStartMouse.lng;
        const deltaLat = e.lngLat.lat - this._dragStartMouse.lat;
        this._overlayData.coordinates = this._dragStartCoords.coordinates.map(coord => [coord[0] + deltaLng, coord[1] + deltaLat]);
        this.updateOverlayAndMarkers();
    },

    onMouseUp() {
        if (!this._isDragging) return;
        this._isDragging = false; this._draggedMarker = null;
        this._map.getCanvas().style.cursor = '';
        this._map.off('mousemove', this.onCornerMouseMove);
        this._map.off('mousemove', this.onCenterMouseMove);
        this._map.off('mouseup', this.onMouseUp);
        this._map.dragPan.enable();
    },

    rotateOverlay(angleDeg) {
        if (!this._overlayData) return;
        const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
        this._overlayData.coordinates = this._overlayData.coordinates.map(coord => {
            const dest = turfDestination(center, turfDistance(center, coord), turfBearing(center, coord) + angleDeg);
            return dest.geometry.coordinates;
        });
        this.updateOverlayAndMarkers();
    },

    scaleOverlay(factor) {
        if (!this._overlayData) return;
        const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
        this._overlayData.coordinates = this._overlayData.coordinates.map(coord => {
            const dx = coord[0] - center[0];
            const dy = coord[1] - center[1];
            return [center[0] + dx * factor, center[1] + dy * factor];
        });
        this.updateOverlayAndMarkers();
    },

    updateOverlayAndMarkers() {
        if (this._onOverlayUpdate && this._overlayData) this._onOverlayUpdate(this._overlayData);
        this._markers.forEach((m, i) => m.setLngLat(this._overlayData.coordinates[i]));
        if (this._centerMarker) this._centerMarker.setLngLat(turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates);
    },

    async save() {
        if (this._overlayData) {
            try {
                await this._saveToDB(this._overlayData);
                this.deactivate();
            } catch (e) {
                console.error('Failed to save to IndexedDB:', e);
                alert('Ошибка при сохранении: файл слишком велик даже для базы данных.');
            }
        }
    },

    cancel() {
        if (this._originalCoordinates) {
            this._overlayData.coordinates = this._originalCoordinates;
            this._onOverlayUpdate(this._overlayData);
        } else {
            this._onOverlayUpdate(null);
            this._overlayData = null;
        }
        this.deactivate();
    },

    async remove() {
        try {
            await this._removeFromDB();
            this._onOverlayUpdate(null);
            this._overlayData = null;
            this.deactivate();
        } catch (e) {
            console.error('Failed to remove from IndexedDB:', e);
        }
    }
};